import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { WorkerStatusTypesLookup } from '@eluve/graphql-types';
import { specSchema } from '@eluve/sdui-types';

const c = initContract();

export const previewFormatSchema = z.enum(['paragraph', 'bullet_points']);
export const previewDetailLevelSchema = z.enum([
  'concise',
  'standard',
  'detailed',
]);

export type PreviewFormat = z.infer<typeof previewFormatSchema>;
export type PreviewDetailLevel = z.infer<typeof previewDetailLevelSchema>;

const generateInsightsPreviewRequestSchema = z.object({
  format: previewFormatSchema,
  detailLevel: previewDetailLevelSchema,
  customInstructions: z.string().nullable(),
  appointmentId: z.string().uuid().nullable(),
});

export type GenerateInsightsPreviewRequest = z.infer<
  typeof generateInsightsPreviewRequestSchema
>;

export const previewCandidateAppointmentSchema = z.object({
  appointmentId: z.string().uuid(),
  patientId: z.string().uuid(),
  patientName: z.string(),
  startDate: z.string(),
  source: z.enum(['today', 'recent']),
});

export type PreviewCandidateAppointment = z.infer<
  typeof previewCandidateAppointmentSchema
>;

const listPreviewCandidatesResponseSchema = z.object({
  candidates: z.array(previewCandidateAppointmentSchema),
});

const generateInsightsPreviewCompletedResponseSchema = z.object({
  cachedOutputId: z.string(),
  status: z.literal(WorkerStatusTypesLookup.COMPLETED),
  spec: specSchema,
});

const generateInsightsPreviewPendingResponseSchema = z.object({
  cachedOutputId: z.string(),
  status: z.enum([
    WorkerStatusTypesLookup.NOT_STARTED,
    WorkerStatusTypesLookup.IN_PROGRESS,
  ]),
});

export type GenerateInsightsPreviewCompletedResponse = z.infer<
  typeof generateInsightsPreviewCompletedResponseSchema
>;

export type GenerateInsightsPreviewPendingResponse = z.infer<
  typeof generateInsightsPreviewPendingResponseSchema
>;

export const insightsPreviewContract = c.router(
  {
    generate: {
      method: 'POST',
      path: 'generate',
      body: generateInsightsPreviewRequestSchema,
      responses: {
        200: generateInsightsPreviewCompletedResponseSchema,
        202: generateInsightsPreviewPendingResponseSchema,
      },
      summary:
        'Generate (or look up cached) Custom Insights preview for the current user. Returns 200 with the spec on cache hit, 202 with a cachedOutputId to subscribe to on cache miss.',
    },
    listCandidates: {
      method: 'GET',
      path: 'candidate-appointments',
      query: z.object({
        // IANA timezone (e.g. 'America/Los_Angeles') used to evaluate the
        // "today" tier. Defaults to UTC when omitted, which can skew the
        // tier-1 window for non-UTC users (e.g. a Pacific-time doctor's
        // late-day appointments would still match "today" but their
        // morning appointments could fall out before local midnight).
        timezone: z.string().optional(),
      }),
      responses: {
        200: listPreviewCandidatesResponseSchema,
      },
      summary:
        'Up to 3 candidate appointments to use as PVI preview context. Tier 1 prefers patients the doctor is seeing today who already have prior charts; Tier 2 falls back to the most recent patients with multiple charts. Returns an empty list when nothing eligible — frontend treats that as sample-only.',
    },
  },
  {
    pathPrefix: '/:tenantId/insights-preview/',
  },
);
